/**
 * Small arms and what they hit. Three timbres must never be confused (01 §10): the ranger's
 * own carbine (bright, tight), an enemy rifle in the open (lower, boomier, a longer tail), and
 * fire from INSIDE a fortification - `mgInside` - which is dull and boxy, rings at the
 * bunker's own pitch and flutters off its walls. You know a nest has opened up before you see it.
 */

import { PRIORITY, type SfxDef } from './types';

export const WEAPON_SFX = {
  carbine: {
    variants: 4, priority: PRIORITY.action, gain: 0.8, maxDistance: 120, rateSpread: 0.03,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.06, curve: 3, gain: 1, filter: { type: 'hp', cutoff: 1800, cutoffEnd: 700 } },
        { wave: 'noise', decay: 0.17, curve: 2.5, gain: 0.85,
          filter: { type: 'bp', cutoff: 1100, cutoffEnd: 320, q: 1.1 } },
        { wave: 'sine', freq: 210, freqEnd: 58, sweep: 0.45, decay: 0.12, gain: 0.9 },
        { wave: 'pink', at: 0.012, attack: 0.01, decay: 0.38, curve: 2.2, gain: 0.3,
          filter: { type: 'lp', cutoff: 2600, cutoffEnd: 500 } },
      ],
      jitter: { pitch: 0.06, time: 0.12, gain: 0.12 }, drive: 1.8,
    },
  },
  enemyRifle: {
    variants: 4, priority: PRIORITY.cue, gain: 0.85, maxDistance: 120, rateSpread: 0.04,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.07, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 1300, cutoffEnd: 500 } },
        { wave: 'noise', decay: 0.25, curve: 2.2, gain: 1,
          filter: { type: 'bp', cutoff: 700, cutoffEnd: 210, q: 1.3 } },
        { wave: 'sine', freq: 160, freqEnd: 46, sweep: 0.5, decay: 0.17, gain: 1 },
        { wave: 'pink', at: 0.015, attack: 0.012, decay: 0.6, curve: 2, gain: 0.42,
          filter: { type: 'lp', cutoff: 1800, cutoffEnd: 320 } },
      ],
      jitter: { pitch: 0.07, time: 0.12, gain: 0.12 }, drive: 2.2,
    },
  },
  mgInside: {
    variants: 4, priority: PRIORITY.cue, gain: 0.95, maxDistance: 120, rateSpread: 0.025,
    patch: {
      layers: [
        { wave: 'sine', freq: 125, freqEnd: 50, sweep: 0.5, decay: 0.15, gain: 1 },
        // The box itself: two ringing resonances, which is what reads as "indoors".
        { wave: 'noise', decay: 0.22, curve: 2, gain: 1.1, filter: { type: 'bp', cutoff: 340, q: 7 } },
        { wave: 'noise', decay: 0.13, curve: 2, gain: 0.55, filter: { type: 'bp', cutoff: 730, q: 5 } },
        // Enough of a crack left that it still reads as gunfire, heard through a firing slit.
        { wave: 'noise', decay: 0.05, curve: 3, gain: 0.75, filter: { type: 'lp', cutoff: 2300 } },
        { wave: 'noise', decay: 0.035, curve: 3, gain: 0.5, filter: { type: 'bp', cutoff: 1500, q: 2 } },
      ],
      jitter: { pitch: 0.04, time: 0.08, gain: 0.1 }, drive: 2, echo: { time: 0.021, gain: 0.55, count: 3 },
    },
  },
  dryFire: {
    variants: 2, priority: PRIORITY.action, gain: 0.5,
    patch: {
      layers: [
        { wave: 'sine', freq: 2300, fmRatio: 1.71, fmIndex: 2.2, fmDecay: 0.008, decay: 0.03, curve: 3, gain: 1 },
        { wave: 'noise', decay: 0.012, curve: 3, gain: 0.6, filter: { type: 'hp', cutoff: 3200 } },
        { wave: 'sine', at: 0.055, freq: 1500, fmRatio: 2.3, fmIndex: 1.4, fmDecay: 0.01, decay: 0.035, curve: 3,
          gain: 0.5 },
      ],
      jitter: { pitch: 0.04 },
    },
  },
  reload: {
    variants: 2, priority: PRIORITY.action, gain: 0.5,
    patch: {
      layers: [
        // Magazine out, magazine home, bolt forward: spread over the 1.8 s the reload takes.
        { wave: 'noise', decay: 0.05, curve: 3, gain: 0.8, filter: { type: 'bp', cutoff: 1900, q: 3 } },
        { wave: 'sine', freq: 320, freqEnd: 210, decay: 0.045, gain: 0.5 },
        { wave: 'noise', at: 0.62, decay: 0.06, curve: 3, gain: 1, filter: { type: 'bp', cutoff: 950, q: 2.5 } },
        { wave: 'sine', at: 0.62, freq: 190, freqEnd: 120, decay: 0.07, gain: 0.8 },
        { wave: 'sine', at: 1.18, freq: 1500, fmRatio: 2.4, fmIndex: 2, fmDecay: 0.012, decay: 0.04, curve: 3,
          gain: 0.8 },
        { wave: 'sine', at: 1.3, freq: 1150, fmRatio: 1.6, fmIndex: 2.4, fmDecay: 0.015, decay: 0.06, curve: 3,
          gain: 1 },
        { wave: 'noise', at: 1.3, decay: 0.04, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 2500 } },
      ],
      jitter: { pitch: 0.03, time: 0.04 },
    },
  },
  knifeSwing: {
    variants: 3, priority: PRIORITY.action, gain: 0.4, rateSpread: 0.08,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.04, decay: 0.12, gain: 1,
          filter: { type: 'bp', cutoff: 700, cutoffEnd: 3200, q: 2.2 } },
      ],
      jitter: { pitch: 0.1, time: 0.15 },
    },
  },
  knifeHit: {
    variants: 3, priority: PRIORITY.action, gain: 0.55, rateSpread: 0.06,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.025, decay: 0.07, gain: 0.5,
          filter: { type: 'bp', cutoff: 900, cutoffEnd: 3000, q: 2.2 } },
        { wave: 'sine', at: 0.06, freq: 150, freqEnd: 60, sweep: 0.6, decay: 0.11, gain: 1 },
        { wave: 'noise', at: 0.06, decay: 0.06, curve: 2.5, gain: 0.8, filter: { type: 'lp', cutoff: 950 } },
        { wave: 'noise', at: 0.065, decay: 0.03, curve: 3, gain: 0.45, filter: { type: 'hp', cutoff: 2600 } },
      ],
      jitter: { pitch: 0.08, time: 0.1 },
    },
  },
  grenadeThrow: {
    variants: 2, priority: PRIORITY.action, gain: 0.45,
    patch: {
      layers: [
        // The pin, then the arm going over.
        { wave: 'sine', freq: 3100, fmRatio: 2.31, fmIndex: 1.6, fmDecay: 0.02, decay: 0.07, curve: 3, gain: 0.7 },
        { wave: 'noise', at: 0.08, attack: 0.06, decay: 0.2, gain: 1,
          filter: { type: 'bp', cutoff: 500, cutoffEnd: 1900, q: 1.8 } },
      ],
      jitter: { pitch: 0.06, time: 0.1 },
    },
  },
  lawLaunch: {
    variants: 2, priority: PRIORITY.action, gain: 0.95, maxDistance: 140,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.004, decay: 0.9, curve: 1.6, gain: 1,
          filter: { type: 'lp', cutoff: 6500, cutoffEnd: 380 } },
        { wave: 'saw', freq: 92, freqEnd: 40, decay: 0.6, gain: 0.7, drive: 2.5, filter: { type: 'lp', cutoff: 500 } },
        { wave: 'sine', freq: 115, freqEnd: 34, sweep: 0.5, decay: 0.26, gain: 1 },
        { wave: 'noise', at: 0.04, attack: 0.05, decay: 1.1, gain: 0.5,
          filter: { type: 'bp', cutoff: 2600, cutoffEnd: 850, q: 1 } },
      ],
      jitter: { pitch: 0.04, time: 0.06 }, drive: 1.6,
    },
  },
  impactDirt: {
    variants: 4, priority: PRIORITY.detail, gain: 0.4, minInterval: 0.03, rateSpread: 0.12,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.07, curve: 2.5, gain: 1, filter: { type: 'bp', cutoff: 950, cutoffEnd: 300, q: 1 } },
        { wave: 'noise', decay: 0.02, curve: 3, gain: 0.5, filter: { type: 'hp', cutoff: 3000 } },
        { wave: 'sine', freq: 130, freqEnd: 60, decay: 0.05, gain: 0.5 },
      ],
      jitter: { pitch: 0.15, time: 0.2, gain: 0.2 },
    },
  },
  impactHard: {
    variants: 4, priority: PRIORITY.detail, gain: 0.42, minInterval: 0.03, rateSpread: 0.12,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.03, curve: 3, gain: 1, filter: { type: 'hp', cutoff: 2400 } },
        { wave: 'sine', freq: 1700, fmRatio: 2.76, fmIndex: 2.6, fmDecay: 0.02, decay: 0.09, curve: 3, gain: 0.6 },
        { wave: 'noise', decay: 0.08, curve: 2.5, gain: 0.6, filter: { type: 'bp', cutoff: 1300, q: 2 } },
      ],
      jitter: { pitch: 0.2, time: 0.2, gain: 0.2 },
    },
  },
  ricochet: {
    variants: 3, priority: PRIORITY.detail, gain: 0.3, minInterval: 0.12, rateSpread: 0.2,
    patch: {
      layers: [
        { wave: 'sine', freq: 2900, freqEnd: 1100, sweep: 0.7, vibHz: 31, vibDepth: 0.6, attack: 0.004, decay: 0.34,
          gain: 1 },
        { wave: 'noise', decay: 0.02, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 3500 } },
      ],
      jitter: { pitch: 0.25, time: 0.2 },
    },
  },
} satisfies Record<string, SfxDef>;
